import React, { useState, useEffect, useReducer, useContext, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    minHeight: 0, // Importante para flexbox
  },

  ticketsList: {
    flex: 1,
    maxHeight: "100%",
    overflowY: "auto", // Cambiado de "scroll" a "auto" para mejor comportamiento
    overflowX: "hidden", // Evitar scroll horizontal
    ...theme.scrollbarStyles,
    borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    minHeight: 0, // Importante para flexbox
  },

  ticketsListHeader: {
    color: "rgb(67, 83, 105)",
    zIndex: 2,
    backgroundColor: "white",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  ticketsCount: {
    fontWeight: "normal",
    color: "rgb(104, 121, 146)",
    marginLeft: "8px",
    fontSize: "14px",
  },

  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  // ✅ ESTILOS PARA EL BUSCADOR
  searchInputWrapper: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    padding: "8px 12px",
    margin: "8px",
    border: "1px solid #e0e0e0",
    transition: "all 0.3s ease",
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "#fff",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
  },

  searchIcon: {
    color: "#666",
    marginRight: "8px",
    fontSize: "20px",
  },

  searchInput: {
    flex: 1,
    fontSize: "14px",
    "& input": {
      padding: "4px 0",
      fontSize: "14px",
    },
    "& input::placeholder": {
      color: "#999",
      fontSize: "14px",
    },
  },

  searchSwitch: {
    marginLeft: "8px",
    "& .MuiSwitch-thumb": {
      width: "16px",
      height: "16px",
    },
    "& .MuiSwitch-track": {
      height: "12px",
      borderRadius: "6px",
    },
  },

  // ✅ ESTILOS PARA LA BARRA DE HERRAMIENTAS
  quickActionsBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px",
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #e0e0e0",
    minHeight: "56px",
  },

  quickActionsLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  quickActionsRight: {
    display: "flex",
    alignItems: "center",
  },

  quickActionButton: {
    minWidth: "40px",
    height: "40px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    backgroundColor: "#fff",
    color: "#666",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      borderColor: theme.palette.primary.main,
    },
  },

  quickActionIconButton: {
    minWidth: "40px",
    height: "40px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    backgroundColor: "#fff",
    color: "#666",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: theme.palette.primary.main,
    },
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;

    // ✅ DEBUG logs removidos para producción

    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });

    return [...state];
  }

  if (action.type === "ADD_TICKET") {
    const ticket = action.payload;
    
    // ✅ VERIFICAR SI EL TICKET YA EXISTE PARA EVITAR DUPLICADOS
    const existingTicketIndex = state.findIndex((t) => t.id === ticket.id);
    if (existingTicketIndex !== -1) {
      // ✅ SI YA EXISTE, ACTUALIZAR EN LUGAR DE DUPLICAR (usar misma lógica que UPDATE_TICKET)
      const newTags = Array.isArray(ticket.tags) 
        ? ticket.tags.map(tag => ({ ...tag }))
        : (ticket.tags || []);
      
      state[existingTicketIndex] = {
        ...ticket,
        tags: newTags, // ✅ NUEVA REFERENCIA para forzar re-render
        contact: ticket.contact ? { ...ticket.contact } : ticket.contact,
        user: ticket.user ? { ...ticket.user } : ticket.user
      };
      return [...state];
    }
    
    // ✅ SI NO EXISTE, AGREGAR AL INICIO
    // ✅ Asegurar que tags tenga nueva referencia también al agregar
    const newTags = Array.isArray(ticket.tags) 
      ? ticket.tags.map(tag => ({ ...tag }))
      : (ticket.tags || []);
    
    const ticketWithNewRefs = {
      ...ticket,
      tags: newTags,
      contact: ticket.contact ? { ...ticket.contact } : ticket.contact,
      user: ticket.user ? { ...ticket.user } : ticket.user
    };
    
    return [ticketWithNewRefs, ...state];
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    
    if (ticketIndex !== -1) {
      // ✅ FIX 26/12/2025: Crear nuevo objeto para forzar re-render de React
      // Asegurar que todas las propiedades relevantes sean nuevas referencias
      // ✅ CRÍTICO: Crear nueva referencia para tags para que React detecte el cambio
      const newTags = Array.isArray(ticket.tags) 
        ? ticket.tags.map(tag => ({ ...tag })) // Crear nuevos objetos para cada tag
        : (ticket.tags || []);
      
      // ✅ CRÍTICO: Usar updatedAt del ticket si viene del backend, sino usar fecha actual
      // El backend ahora actualiza updatedAt explícitamente, así que debemos respetarlo
      const ticketUpdatedAt = ticket.updatedAt ? new Date(ticket.updatedAt).toISOString() : new Date().toISOString();
      
      const updatedTicket = {
        ...ticket,
        tags: newTags, // ✅ NUEVA REFERENCIA para forzar re-render
        lastMessage: ticket.lastMessage ? (typeof ticket.lastMessage === 'string' ? ticket.lastMessage : { ...ticket.lastMessage }) : ticket.lastMessage,
        updatedAt: ticketUpdatedAt, // ✅ Usar updatedAt del backend (actualizado explícitamente)
        // ✅ Asegurar que contact y user también sean nuevas referencias
        contact: ticket.contact ? { ...ticket.contact } : ticket.contact,
        user: ticket.user ? { ...ticket.user } : ticket.user
      };
      
      // ✅ LOG PARA DEBUG (solo si es necesario)
      // console.log(`[TicketsListCustom Reducer] 🔄 UPDATE_TICKET - Ticket ${ticket.id}:`, {
      //   oldTagsCount: state[ticketIndex].tags?.length || 0,
      //   newTagsCount: newTags.length,
      //   oldTags: state[ticketIndex].tags?.map(t => t.id) || [],
      //   newTags: newTags.map(t => t.id),
      //   updatedAt: ticketUpdatedAt
      // });
      
      state[ticketIndex] = updatedTicket;
    } else {
      state.unshift(ticket);
    }

    // ✅ REORDENAR POR updatedAt EN ORDEN DESCENDENTE
    const sortedTickets = [...state].sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return dateB - dateA; // DESC: más reciente primero
    });
    
    return sortedTickets;
  }

  // ✅ AGREGADO: Actualizar solo las reacciones del ticket
  if (action.type === "UPDATE_TICKET_REACTIONS") {
    const { ticketId, lastMessageWithReactions } = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex] = {
        ...state[ticketIndex],
        lastMessageWithReactions: lastMessageWithReactions
      };
    } 

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
      state.unshift(state.splice(ticketIndex, 1)[0]);
    } else {
      state.unshift(ticket);
    }

    // ✅ REORDENAR POR updatedAt EN ORDEN DESCENDENTE
    const sortedTickets = [...state].sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return dateB - dateA; // DESC: más reciente primero
    });
    
    return sortedTickets;
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    // ✅ FIX 05/12/2025: Buscar por contactId o contact.id como fallback
    const ticketIndex = state.findIndex((t) => 
      t.contactId === contact.id || t.contact?.id === contact.id
    );
    if (ticketIndex !== -1) {
      // ✅ FIX 05/12/2025: Crear nuevo objeto para forzar re-render de React
      state[ticketIndex] = {
        ...state[ticketIndex],
        contact: { ...contact } // Crear nuevo objeto contact
      };
    }
    return [...state];
  }

  if (action.type === "UPDATE_TICKET_PRESENCE") {
    const data = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === data.ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].presence = data.presence;
      
      // ✅ TIMEOUT: Ocultar "Digitando..." después de 10 segundos si es "composing"
      if (data.presence === "composing") {
        setTimeout(() => {
          const currentTicketIndex = state.findIndex((t) => t.id === data.ticketId);
          if (currentTicketIndex !== -1 && state[currentTicketIndex].presence === "composing") {
            state[currentTicketIndex].presence = "available";
            // ✅ Forzar re-render
            return [...state];
          }
        }, 10000); // 10 segundos
      }
    } else {
      // ❌ TICKET NO ENCONTRADO PARA PRESENCE:
    }
    return [...state];
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    // ✅ LOGS REMOVIDOS PARA USUARIO FINAL
    
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      // ✅ DELETE_TICKET reducer: Ticket eliminado
      state.splice(ticketIndex, 1);
    } else {
      // ✅ LOGS REMOVIDOS PARA USUARIO FINAL
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsListCustom = (props) => {
  // ✅ TODOS LOS HOOKS DEBEN IR AL INICIO - SIN EXCEPCIONES
  const {
    status,
    searchParam,
    tags,
    users,
    showAll,
    selectedQueueIds,
    updateCount,
    style,
    aiAgentFilter, // ✅ NUEVO PROP PARA FILTRAR AGENTES IA
    excludeAIAgents, // ✅ NUEVO PROP PARA EXCLUIR AGENTES IA
    searchOnMessages, // ✅ NUEVO PROP PARA BUSCAR EN MENSAJES
    isActiveTab = true, // ✅ NUEVO PROP PARA INDICAR SI ES LA PESTAÑA ACTIVA
    sortAscending = false, // ✅ NUEVO PROP PARA ORDENAMIENTO
  } = props || {};
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  // Mantener una referencia siempre actualizada de la lista para usarla en callbacks de socket
  const ticketsListRef = useRef([]);
  useEffect(() => {
    ticketsListRef.current = ticketsList;
  }, [ticketsList]);
  const { user } = useContext(AuthContext);
  const { profile, queues } = user || {};
  const isMounted = useRef(true);
  const presenceTimeouts = useRef({});
  const socketManager = useContext(SocketContext);
  
  // ✅ MOVER TODOS LOS HOOKS AL INICIO - ANTES DE CUALQUIER VERIFICACIÓN CONDICIONAL
  // ✅ Normalizar selectedQueueIds para evitar reseteos innecesarios
  const normalizedQueueIds = selectedQueueIds && selectedQueueIds.length > 0 ? selectedQueueIds : null;
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [status, searchParam, dispatch, showAll, tags, users, normalizedQueueIds, aiAgentFilter, excludeAIAgents]); // ✅ Solo resetear si hay filtros activos


  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam: isActiveTab ? searchParam : "", // ✅ Solo buscar si es la pestaña activa
    status,
    showAll,
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    queueIds: selectedQueueIds && selectedQueueIds.length > 0 ? JSON.stringify(selectedQueueIds) : undefined, // ✅ Solo pasar queueIds si hay filtros activos
    searchOnMessages: searchOnMessages,
    sortAscending: sortAscending, // ✅ AGREGADO: Parámetro de ordenamiento
  });

  useEffect(() => {
    const queueIds = queues?.map((q) => q.id) || [];
    
    // ✅ OPTIMIZADO: Logs solo en desarrollo o cuando sea necesario
    // console.log(`[TicketsListCustom] 📥 Cargando tickets desde backend para pestaña "${status}":`, tickets.length);
    
    // ✅ CORRECCIÓN 2025-12-23: Aplicar filtros de seguridad en frontend como capa adicional
    // El backend ya filtra correctamente, pero esto es defensa en profundidad
    let filteredTickets = tickets;
    
    // ✅ Si el usuario NO es admin, SIEMPRE filtrar por departamento (incluso si showAll es true)
    if (profile !== "admin") {
      if (queueIds.length > 0) {
        // ✅ Filtrar por departamentos asignados
        filteredTickets = tickets.filter(
          (t) => {
            // ✅ Si el ticket tiene departamento, debe estar en los departamentos del usuario
            if (t.queueId) {
              return queueIds.indexOf(t.queueId) > -1;
            }
            // ✅ Si el ticket NO tiene departamento, solo mostrarlo si allTicket = enabled
            const showTicketWithoutQueue = user?.allTicket === 'enabled';
            return showTicketWithoutQueue;
          }
        );
      } else {
        // ✅ Si el usuario no tiene departamentos asignados, solo mostrar tickets sin departamento si allTicket = enabled
        const showTicketWithoutQueue = user?.allTicket === 'enabled';
        if (showTicketWithoutQueue) {
          filteredTickets = tickets.filter((t) => !t.queueId);
        } else {
          filteredTickets = []; // ✅ No mostrar tickets si no tiene departamentos y allTicket = disabled
        }
      }
    } else {
      // ✅ ADMIN: Puede ver todos los tickets sin filtrar
      filteredTickets = tickets;
    }

    // ✅ APLICAR FILTRO DE AGENTES IA SI ES NECESARIO
    if (aiAgentFilter) {
      filteredTickets = filteredTickets.filter(ticket => isAIAgent(ticket));
    }
    
    // ✅ EXCLUIR AGENTES IA SI ES NECESARIO
    if (excludeAIAgents) {
      filteredTickets = filteredTickets.filter(ticket => !isAIAgent(ticket));
    }
    
    // ✅ CORRECCIÓN 2025-12-23: Solo admins pueden ver todos los tickets sin filtrar
    // ✅ USERS siempre ven solo sus departamentos asignados (defensa en profundidad)
    const ticketsToDispatch = filteredTickets;
    
    // ✅ OPTIMIZADO: Logs solo en desarrollo
    // console.log(`[TicketsListCustom] 📤 Despachando ${ticketsToDispatch.length} tickets para pestaña "${status}"`);
    
    dispatch({ type: "LOAD_TICKETS", payload: ticketsToDispatch });
  }, [tickets, status, searchParam, queues, profile, aiAgentFilter, excludeAIAgents, showAll, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ NUEVO: LIMPIEZA AUTOMÁTICA DE TICKETS INCORRECTOS
  useEffect(() => {
    if (!ticketsList || ticketsList.length === 0) return;
    
    // ✅ CORREGIDO: No limpiar tickets cuando status === "all"
    if (status === "all") {
      return; // ✅ No eliminar tickets en la vista "all"
    }
    
    const ticketsToRemove = [];
    
    ticketsList.forEach(ticket => {
      const ticketHasAI = isAIAgent(ticket);
      const ticketStatus = ticket.status;
      const isGroupTicket = ticket.contact && ticket.contact.isGroup;
      
      let shouldBeRemoved = false;
      
      // ✅ REGLA 1: Si estamos en pestaña "ai" pero el ticket NO tiene IA
      if (status === "ai" && !ticketHasAI) {
        shouldBeRemoved = true;
      }
      // ✅ REGLA 2: Si estamos en pestaña "group" pero el ticket NO es de grupo O está cerrado
      // ✅ CRÍTICO: Los grupos cerrados NO deben estar en la pestaña "group"
      else if (status === "group" && (!isGroupTicket || ticketStatus === "closed")) {
        shouldBeRemoved = true;
      }
      // ✅ REGLA 3: Si estamos en pestaña "closed", NO excluir grupos ni IA
      else if (status === "closed") {
        // ✅ En pestaña "closed", solo verificar que el status sea "closed"
        if (ticketStatus !== "closed") {
          shouldBeRemoved = true;
        }
        // ✅ NO excluir grupos ni IA en la pestaña "closed"
      }
      // ✅ REGLA 4: Si estamos en pestañas normales (open/pending) pero el ticket SÍ tiene IA
      else if (status !== "ai" && status !== "group" && status !== "closed" && ticketHasAI) {
        shouldBeRemoved = true;
      }
      // ✅ REGLA 5: Si estamos en pestañas normales (open/pending) pero el ticket NO coincide con el status
      // ✅ CORREGIDO: Solo aplicar cuando status NO es "all" ni "closed"
      else if (status !== "ai" && status !== "group" && status !== "closed" && status !== "all" && ticketStatus !== status) {
        shouldBeRemoved = true;
      }
      // ✅ REGLA 6: Si estamos en pestañas normales (open/pending) pero el ticket SÍ es de grupo
      else if (status !== "ai" && status !== "group" && status !== "closed" && isGroupTicket) {
        shouldBeRemoved = true;
      }
      
      if (shouldBeRemoved) {
        ticketsToRemove.push(ticket.id);
      }
    });
    
    // ✅ REMOVER TICKETS INCORRECTOS
    if (ticketsToRemove.length > 0) {
      ticketsToRemove.forEach(ticketId => {
        dispatch({ type: "DELETE_TICKET", payload: ticketId });
      });
    }
  }, [ticketsList, status]); // eslint-disable-line react-hooks/exhaustive-deps


  // ✅ CLEANUP DEL COMPONENTE
  useEffect(() => {
    return () => {
      isMounted.current = false;
      // ✅ LIMPIAR TODOS LOS TIMEOUTS AL DESMONTAR
      Object.values(presenceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
      presenceTimeouts.current = {};
    };
  }, []);

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

  // ✅ SOLUCIÓN SIMPLE: REORDENAR AUTOMÁTICAMENTE CADA 2 SEGUNDOS
  useEffect(() => {
            const interval = setInterval(() => {
          if (ticketsList.length > 0) {
            const sortedTickets = [...ticketsList].sort((a, b) => {
              const dateA = new Date(a.updatedAt);
              const dateB = new Date(b.updatedAt);
              return dateB - dateA; // DESC: más reciente primero
            });
            
            // Solo actualizar si el orden cambió
            const currentOrder = ticketsList.map(t => t.id).join(',');
            const newOrder = sortedTickets.map(t => t.id).join(',');
            
            if (currentOrder !== newOrder) {
              dispatch({ type: "LOAD_TICKETS", payload: sortedTickets });
            }
          }
        }, 2000); // Cada 2 segundos

    return () => clearInterval(interval);
  }, [ticketsList, dispatch]);
  
  // ✅ FUNCIÓN PARA IDENTIFICAR AGENTES IA - LÓGICA CORREGIDA (FASE 1)
  // ✅ CORRECCIÓN 30/12/2025: Incluir TODOS los tipos de automatización
  // ✅ MODELO DE NEGOCIO APROBADO: promptId != null OR chatbot = true OR integrationId != null
  // ✅ FUNCIÓN PARA IDENTIFICAR AGENTES IA - INCLUYE FLOWBUILDER
  const isAIAgent = (ticket) => {
    if (!ticket) return false;
    
    // ✅ CRITERIOS PARA DETECTAR TICKET CON AUTOMATIZACIÓN:
    // 1. promptId != null → IA real (OpenAI, OpenRouter)
    // 2. chatbot = true → Chatbot de menú
    // 3. integrationId != null → Integraciones externas (Typebot, n8n)
    // 4. flowStatus != null → FlowBuilder (canal independiente)
    return (
      ticket.promptId != null ||
      ticket.chatbot === true ||
      ticket.integrationId != null ||
      ticket.flowStatus != null
    );
  };

  useEffect(() => {
    isMounted.current = true;
    
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    
    // ✅ Socket configurado correctamente

    const shouldUpdateTicket = (ticket) => {
      // ✅ 1. VERIFICAR PERMISOS DE USUARIO
      // ✅ CORRECCIÓN: Comparar números como números, no como strings
      const ticketUserIdNum = ticket.userId ? Number(ticket.userId) : null;
      const currentUserIdNum = user?.id ? Number(user?.id) : null;
      const hasPermission = showAll || ticketUserIdNum === currentUserIdNum || !ticket.userId;
      if (!hasPermission) {
        return false;
      }
      
      // ✅ 2. VERIFICAR STATUS DEL TICKET (EXCEPTO PARA PESTAÑA AI Y ALL)
      // ✅ FIX 12/04/2025: Excluir grupos de pestañas normales en shouldUpdateTicket
      const isGroupTicket = ticket.contact && ticket.contact.isGroup;
      if (status === "ai") {
        // ✅ Para pestaña "ai", solo verificar que tenga IA configurada
        // ✅ CRÍTICO: Incluir flowStatus para FlowBuilder
        const ticketHasAI = ticket.promptId || ticket.useIntegration || ticket.chatbot || ticket.flowStatus != null;
        if (!ticketHasAI) {
          return false;
        }
      } else if (status === "all") {
        // ✅ Para pestaña "all", mostrar solo tickets open y pending (excluir grupos)
        if ((ticket.status !== "open" && ticket.status !== "pending") || isGroupTicket) {
          return false;
        }
      } else if (status === "group") {
        // ✅ Para pestaña "group", solo grupos ABIERTOS (open/pending)
        // ✅ CRÍTICO: NO incluir grupos cerrados, esos van a la pestaña "closed"
        if (!isGroupTicket || ticket.status === "closed") {
          return false;
        }
      } else if (status === "closed") {
        // ✅ CRÍTICO: Para pestaña "closed", INCLUIR grupos e IA cerrados
        // Los grupos e IA cerrados DEBEN mostrarse en la pestaña de cerrados
        if (ticket.status !== "closed") {
          return false;
        }
        // ✅ NO excluir grupos ni IA en la pestaña "closed"
      } else if (ticket.status !== status || isGroupTicket) {
        // ✅ Para pestañas normales (open/pending), excluir grupos
        return false;
      }
      
      // ✅ 3. VERIFICAR FILTROS DE COLA
      // ✅ CORRECCIÓN v1.3.0: Si showTicketWithoutQueue es true, no filtrar por queueId
      const showTicketWithoutQueue = user?.allTicket === 'enabled';
      if (!showTicketWithoutQueue && selectedQueueIds && selectedQueueIds.length > 0 && ticket.queueId) {
        if (!selectedQueueIds.includes(ticket.queueId)) {
          return false;
        }
      }
      
      return true;
    };

    // const notBelongsToUserQueues = (ticket) =>
    //   ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

    // ✅ SUSCRIPCIÓN INMEDIATA SIN ESPERAR "ready"
    if (status) {
      socket.emit("joinTickets", status);
      // ✅ FASE 1: ELIMINADO - Suscripciones cruzadas que causaban bugs
      // Las pestañas solo deben escuchar SU PROPIO canal
      // El backend ya emite DELETE al canal correcto (oldStatus)
      // El backend ya emite UPDATE al canal correcto (newStatus)
    } else {
      socket.emit("joinNotification");
    }

    socket.on("ready", () => {
      if (status) {
        socket.emit("joinTickets", status);
        // ✅ FASE 1: ELIMINADO - Suscripciones cruzadas
      } else {
        socket.emit("joinNotification");
      }
    });

    socket.on("connect", () => {
      // ✅ REDUCIR SPAM DE LOGS - Solo log en desarrollo
      if ((typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development')) {
        // console.log("🔄 TicketsListCustom: Socket reconectado, re-suscribiendo...");
      }
      
      // ✅ RE-SUSCRIBIRSE A LOS CANALES DESPUÉS DE RECONEXIÓN
      if (status) {
        if ((typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development')) {
          // console.log("🔔 TicketsListCustom: Emitiendo joinTickets para status:", status);
        }
        socket.emit("joinTickets", status);
        // ✅ FASE 1: ELIMINADO - Suscripciones cruzadas que causaban bugs de reabrir tickets
      } else {
        if ((typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development')) {
          // console.log("🔔 TicketsListCustom: Emitiendo joinNotification");
        }
        socket.emit("joinNotification");
      }
      
      // ✅ FASE 1: ELIMINADO - Re-registro de listeners dentro de connect
      // Los listeners ya están registrados FUERA de este callback (línea ~634)
      // Re-registrarlos aquí causaba duplicación de eventos (cada listener se ejecutaba 2 veces)
      // Solo necesitamos re-suscribirnos a canales (joinTickets) arriba ↑
    });

    socket.on("disconnect", (reason) => {
      // console.log("⚠️ TicketsListCustom: Socket desconectado:", reason);
    });

    socket.on(`company-${companyId}-ticket`, (data) => {
      // ✅ PROTECCIÓN CONTRA ERRORES SILENCIOSOS QUE BLOQUEAN LA UI
      try {
        if (!isMounted.current) {
          return;
        }
        
        // ✅ VALIDACIÓN: Verificar que data existe y tiene estructura válida
        if (!data || typeof data !== 'object') {
          console.warn('⚠️ TicketsListCustom: Evento ticket inválido - data no es un objeto:', data);
          return;
        }
        
        // ✅ ACTUALIZAR REACCIONES EN LA VISTA PREVIA
        if (data.action === "update" && data.ticket && data.ticket.lastMessageWithReactions) {
          dispatch({
            type: "UPDATE_TICKET_REACTIONS",
            payload: {
              ticketId: data.ticket.id,
              lastMessageWithReactions: data.ticket.lastMessageWithReactions
            }
          });
          return; // ✅ Salir temprano para evitar procesamiento adicional
        }
      
      // ✅ LÓGICA DE FILTRADO EXCLUSIVA PARA CREACIÓN DE TICKETS
      // ✅ CRÍTICO: Para "create", verificar directamente sin shouldUpdateTicket
      // porque shouldUpdateTicket puede rechazar tickets con flowStatus
      if (data.action === "create") {
        const ticketHasAI = isAIAgent(data.ticket);
        const ticketStatus = data.ticket.status;
        const isGroupTicket = data.ticket.contact && data.ticket.contact.isGroup;
        
        let shouldCreate = false;
        
        // ✅ REGLA 1: PESTAÑA "ai" - SOLO TICKETS CON IA
        if (status === "ai" && ticketHasAI) {
          shouldCreate = true;
          // console.log(`🤖 TicketsListCustom: Agregando ticket con IA a pestaña AGENTES IA - ID: ${data.ticket.id}`);
        }
        // ✅ REGLA 2: PESTAÑA "group" - SOLO TICKETS DE GRUPOS ABIERTOS (open/pending)
        // ✅ CRÍTICO: NO incluir grupos cerrados, esos van a la pestaña "closed"
        else if (status === "group" && isGroupTicket && (ticketStatus === "open" || ticketStatus === "pending")) {
          shouldCreate = true;
          // console.log(`👥 TicketsListCustom: Agregando ticket de grupo a pestaña GRUPOS - ID: ${data.ticket.id}`);
        }
        // ✅ REGLA 3: PESTAÑA "closed" - INCLUIR TODOS LOS TICKETS CERRADOS (grupos e IA incluidos)
        else if (status === "closed" && ticketStatus === "closed") {
          shouldCreate = true;
          // console.log(`📦 TicketsListCustom: Agregando ticket cerrado a pestaña CERRADOS - ID: ${data.ticket.id}, isGroup: ${isGroupTicket}, hasAI: ${ticketHasAI}`);
        }
        // ✅ REGLA 4: PESTAÑAS NORMALES (open/pending) - SOLO TICKETS SIN IA Y SIN GRUPOS
        // ✅ FIX 12/04/2025: Agregar verificación !isGroupTicket para excluir grupos de pestañas normales
        else if (status !== "ai" && status !== "group" && status !== "closed" && !ticketHasAI && !isGroupTicket && ticketStatus === status) {
          shouldCreate = true;
          // console.log(`👤 TicketsListCustom: Agregando ticket humano a pestaña ${status.toUpperCase()} - ID: ${data.ticket.id}`);
        }
        
        // ✅ PREVENIR DUPLICACIÓN: Si el ticket ya existe, no agregarlo
        // ✅ CRÍTICO: También verificar permisos de usuario
        const ticketUserIdNum = data.ticket.userId ? Number(data.ticket.userId) : null;
        const currentUserIdNum = user?.id ? Number(user?.id) : null;
        const hasUserPermission = showAll || ticketUserIdNum === currentUserIdNum || !data.ticket.userId;
        
        if (shouldCreate && hasUserPermission && !ticketsList.find(t => t.id === data.ticket.id)) {
          // console.log(`[TicketsListCustom] ➕ Agregando ticket ${data.ticket.id} a pestaña "${status}" - hasAI: ${ticketHasAI}, flowStatus: ${data.ticket.flowStatus}`);
          dispatch({
            type: "ADD_TICKET",
            payload: data.ticket,
          });
        } else if (shouldCreate && !hasUserPermission) {
          // console.log(`[TicketsListCustom] ⚠️ Ticket ${data.ticket.id} no tiene permisos de usuario`);
        } else if (shouldCreate) {
          // console.log(`[TicketsListCustom] ⚠️ Ticket ${data.ticket.id} ya existe, evitando duplicación`);
        }
      }
      
      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
      }

      // ✅ LÓGICA DE FILTRADO EXCLUSIVA PARA ACTUALIZACIÓN DE TICKETS
      if (data.action === "update") {
        // ✅ OPTIMIZADO: Logs solo en desarrollo
        // console.log(`[TicketsListCustom] 🔄 UPDATE recibido para ticket ${data.ticket?.id}`);
        
        const ticketHasAI = isAIAgent(data.ticket);
        const ticketStatus = data.ticket.status;
        const isGroupTicket = data.ticket.contact && data.ticket.contact.isGroup;

        const existingTicket = (ticketsListRef.current || []).find(t => t.id === data.ticket.id);

        // 🚀 PASO 1: Determinar si el ticket DEBERÍA estar en esta pestaña
        let shouldBeInThisTab = false;
        if (status === "ai") { // Pestaña "Agentes IA"
          shouldBeInThisTab = ticketHasAI;
        } else if (status === "group") { // Pestaña "Grupos"
          // ✅ CRÍTICO: Solo grupos ABIERTOS (open/pending), NO cerrados
          // Los grupos cerrados deben aparecer en la pestaña "closed"
          shouldBeInThisTab = isGroupTicket && (ticketStatus === "open" || ticketStatus === "pending");
        } else if (status === "closed") { // Pestaña "Cerrados"
          // ✅ CRÍTICO: En pestaña "closed", INCLUIR grupos e IA cerrados
          shouldBeInThisTab = ticketStatus === "closed";
        } else if (status !== "ai" && status !== "group" && status !== "closed") { // Pestañas normales (open, pending)
          // ✅ FIX 12/04/2025: Excluir grupos de pestañas normales
          shouldBeInThisTab = !ticketHasAI && !isGroupTicket && ticketStatus === status;
        }

        // 🚀 PASO 2: Verificar permisos de usuario (INDEPENDIENTE del status)
        // ✅ CORRECCIÓN: Verificar permisos directamente, sin usar shouldUpdateTicket
        // porque shouldUpdateTicket también verifica el status, lo cual causa falsos negativos
        const ticketUserIdNum = data.ticket.userId ? Number(data.ticket.userId) : null;
        const currentUserIdNum = user?.id ? Number(user?.id) : null;
        const hasUserPermission = showAll || ticketUserIdNum === currentUserIdNum || !data.ticket.userId;
        
        // 🚀 PASO 3: Decidir la acción final (ADD, UPDATE, DELETE o NADA)
        if (shouldBeInThisTab && hasUserPermission) {
          if (existingTicket) {
            // ✅ OPTIMIZADO: Logs solo en desarrollo
            // console.log(`[TicketsListCustom] ✅ Actualizando ticket ${data.ticket.id} en lista`);
            dispatch({
              type: "UPDATE_TICKET",
              payload: data.ticket,
            });
          } else {
            // ✅ OPTIMIZADO: Logs solo en desarrollo
            // console.log(`[TicketsListCustom] ➕ Agregando ticket ${data.ticket.id} a lista`);
            dispatch({
              type: "ADD_TICKET",
              payload: data.ticket,
            });
          }
        } else {
          // Si el ticket NO debería estar en esta pestaña (o no tiene permisos)
          // Y actualmente existe en la lista, entonces lo removemos.
          if (existingTicket) {
            // ✅ OPTIMIZADO: Logs solo en desarrollo
            // console.log(`[TicketsListCustom] 🗑️ Removiendo ticket ${data.ticket.id} de lista (no cumple filtros)`);
            dispatch({
              type: "DELETE_TICKET",
              payload: data.ticket.id,
            });
          }
        }
      }

      if (data.action === "delete") {
        // ✅ SOLO procesar DELETE si el ticket está en ESTA lista
        // Esto previene que pestañas inactivas eliminen tickets de otras pestañas
        const ticketExists = (ticketsListRef.current || []).some(t => t.id === data.ticketId);
        if (ticketExists) {
          dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
        }
      }
      } catch (error) {
        // ✅ CAPTURAR ERRORES SILENCIOSOS QUE PODRÍAN BLOQUEAR LA UI
        console.error('❌ TicketsListCustom: Error en listener company-ticket:', error, 'Data:', data);
        // ✅ NO PROPAGAR EL ERROR - Permitir que la aplicación continúe funcionando
      }
    });

    socket.on(`company-${companyId}-appMessage`, (data) => {
      // ✅ PROTECCIÓN CONTRA ERRORES SILENCIOSOS QUE BLOQUEAN LA UI
      try {
        if (!isMounted.current) return;
        
        // ✅ VALIDACIÓN: Verificar que data existe y tiene estructura válida
        if (!data || typeof data !== 'object') {
          console.warn('⚠️ TicketsListCustom: Evento appMessage inválido - data no es un objeto:', data);
          return;
        }
        
        const queueIds = queues.map((q) => q.id);
        if (
          profile === "user" &&
          (queueIds.indexOf(data.ticket?.queue?.id) === -1 ||
            data.ticket.queue === null)
        ) {
          return;
        }

      // ✅ FIX 12/04/2025: Excluir grupos de este bloque también
      // ✅ FIX 05/12/2025: Usar optional chaining para evitar crash si data.ticket es undefined
      const isGroupTicket = data.ticket?.contact?.isGroup;
      
      // ✅ FIX 05/12/2025: Relajar filtros para permitir actualizaciones de mensajes
      // Si el mensaje es del usuario actual (fromMe), siempre actualizar
      const isMessageFromMe = data.message?.fromMe === true;
      
      // ✅ FIX 05/12/2025: Verificar si el ticket ya existe en la lista (usar some() para mejor rendimiento)
      const ticketExists = (ticketsListRef.current || []).some(t => t.id === data.ticket?.id);
      
      if (data.action === "create" && data.ticket) {
        // ✅ Si el ticket YA EXISTE en la lista, actualizar siempre (sin filtros restrictivos)
        // Esto asegura que los tickets visibles se actualicen en tiempo real
        if (ticketExists) {
        dispatch({
            type: "UPDATE_TICKET",
          payload: data.ticket,
        });
        }
        // ✅ Si el mensaje es del usuario actual, actualizar siempre (sin filtros restrictivos)
        else if (isMessageFromMe) {
        dispatch({
          type: "UPDATE_TICKET",
          payload: data.ticket,
        });
        } 
        // ✅ Si no es del usuario actual y no existe en la lista, aplicar filtros normales
        else if (shouldUpdateTicket(data.ticket) && (status === undefined || data.ticket.status === status) && !isGroupTicket) {
          dispatch({
            type: "UPDATE_TICKET",
            payload: data.ticket,
          });
        }
      }

      if (data.action === "update" && data.ticket) {
        dispatch({
          type: "UPDATE_TICKET",
          payload: data.ticket,
        });
      }
      } catch (error) {
        // ✅ CAPTURAR ERRORES SILENCIOSOS QUE PODRÍAN BLOQUEAR LA UI
        console.error('❌ TicketsListCustom: Error en listener appMessage:', error, 'Data:', data);
        // ✅ NO PROPAGAR EL ERROR - Permitir que la aplicación continúe funcionando
      }
    });

    socket.on(`company-${companyId}-presence`, (data) => {
      // ✅ PROTECCIÓN CONTRA ERRORES SILENCIOSOS QUE BLOQUEAN LA UI
      try {
        // ✅ VERIFICAR SI EL COMPONENTE ESTÁ MONTADO ANTES DE DISPATCH
        if (!isMounted.current) {
          return;
        }
        
        // ✅ VALIDACIÓN: Verificar que data existe y tiene estructura válida
        if (!data || typeof data !== 'object') {
          console.warn('⚠️ TicketsListCustom: Evento presence inválido - data no es un objeto:', data);
          return;
        }
      
      // ✅ MEJORAR TIMEOUT: Limpiar timeout anterior si existe
      if (data.presence === "composing") {
        const ticketId = data.ticketId;
        const existingTimeout = presenceTimeouts.current[ticketId];
        
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }
        
        // ✅ CREAR NUEVO TIMEOUT
        const newTimeout = setTimeout(() => {
          dispatch({
            type: "UPDATE_TICKET_PRESENCE",
            payload: {
              ticketId: ticketId,
              presence: "available"
            }
          });
        }, 10000); // 10 segundos
        
        // ✅ GUARDAR REFERENCIA DEL TIMEOUT
        presenceTimeouts.current[ticketId] = newTimeout;
      }
      
      dispatch({
        type: "UPDATE_TICKET_PRESENCE",
        payload: data,
      });
      } catch (error) {
        // ✅ CAPTURAR ERRORES SILENCIOSOS QUE PODRÍAN BLOQUEAR LA UI
        console.error('❌ TicketsListCustom: Error en listener presence:', error, 'Data:', data);
        // ✅ NO PROPAGAR EL ERROR - Permitir que la aplicación continúe funcionando
      }
    });

    socket.on(`company-${companyId}-contact`, (data) => {
      // ✅ PROTECCIÓN CONTRA ERRORES SILENCIOSOS QUE BLOQUEAN LA UI
      try {
        // ✅ VERIFICAR SI EL COMPONENTE ESTÁ MONTADO ANTES DE DISPATCH
        if (!isMounted.current) return;
        
        // ✅ VALIDACIÓN: Verificar que data existe y tiene estructura válida
        if (!data || typeof data !== 'object') {
          console.warn('⚠️ TicketsListCustom: Evento contact inválido - data no es un objeto:', data);
          return;
        }
        
        if (data.action === "update") {
          dispatch({
            type: "UPDATE_TICKET_CONTACT",
            payload: data.contact,
          });
        }
      } catch (error) {
        // ✅ CAPTURAR ERRORES SILENCIOSOS QUE PODRÍAN BLOQUEAR LA UI
        console.error('❌ TicketsListCustom: Error en listener contact:', error, 'Data:', data);
        // ✅ NO PROPAGAR EL ERROR - Permitir que la aplicación continúe funcionando
      }
    });

    return () => {
      isMounted.current = false;
      // ✅ SOLO REMOVER LISTENERS, NO DESCONECTAR EL SOCKET COMPARTIDO
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-ticket`);
        socket.off(`company-${companyId}-appMessage`);
        socket.off(`company-${companyId}-presence`);
        socket.off(`company-${companyId}-contact`);
        socket.off("ready");
        socket.off("connect");
        socket.off("disconnect");
      }
    };
  }, [status, showAll, user?.id, selectedQueueIds, tags, users, profile, queues, socketManager, excludeAIAgents]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ CLEANUP DEL COMPONENTE
  useEffect(() => {
    return () => {
      isMounted.current = false;
      // ✅ LIMPIAR TODOS LOS TIMEOUTS AL DESMONTAR
      Object.values(presenceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
      presenceTimeouts.current = {};
    };
  }, []);

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

  // ✅ SOLUCIÓN SIMPLE: REORDENAR AUTOMÁTICAMENTE CADA 2 SEGUNDOS
  useEffect(() => {
            const interval = setInterval(() => {
          if (ticketsList.length > 0) {
            const sortedTickets = [...ticketsList].sort((a, b) => {
              const dateA = new Date(a.updatedAt);
              const dateB = new Date(b.updatedAt);
              return dateB - dateA; // DESC: más reciente primero
            });
            
            // Solo actualizar si el orden cambió
            const currentOrder = ticketsList.map(t => t.id).join(',');
            const newOrder = sortedTickets.map(t => t.id).join(',');
            
            if (currentOrder !== newOrder) {
              dispatch({ type: "LOAD_TICKETS", payload: sortedTickets });
            }
          }
        }, 2000); // Cada 2 segundos

    return () => clearInterval(interval);
  }, [ticketsList, dispatch]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  // ✅ VERIFICACIONES DE SEGURIDAD DESPUÉS DE TODOS LOS HOOKS
  if (!props) {
    console.error("❌ TicketsListCustom: props es undefined");
    return null;
  }
  
  if (!user) {
    console.error("❌ TicketsListCustom: user es undefined");
    return null;
  }
  
  if (!queues || !Array.isArray(queues)) {
    console.error("❌ TicketsListCustom: queues es undefined o no es array:", queues);
    return null;
  }

  return (
    <Paper className={classes.ticketsListWrapper} style={style}>
      <Paper
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t("ticketsList.noTicketsTitle")}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t("ticketsList.noTicketsMessage")}
              </p>
            </div>
          ) : (
            <>
              {ticketsList.map((ticket) => (
                <TicketListItem 
                  ticket={ticket} 
                  status={status}
                  key={`${ticket.id}-${ticket.updatedAt || ticket.lastMessage?.id || Date.now()}`} 
                />
              ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsListCustom;
